//
// ============================================================================
// TYPES
// ============================================================================
//

/**
 * Error type classification for different handling strategies
 */
export type ErrorType =
  | "abort" // Request was cancelled (user navigation, abort signal)
  | "network" // Network/connectivity issues (CORS, offline, DNS)
  | "server" // Server errors (500, 502, 503, 504)
  | "auth" // Authentication/authorization errors (401, 403)
  | "validation" // Business logic validation errors (400)
  | "unknown"; // Unclassified errors

/**
 * Options for configuring error handling behavior
 */
export interface ErrorHandlerOptions {
  /**
   * Operation name for logging and debugging
   * Example: "fetchPendingSamples", "acceptResult", "updateBarcode"
   */
  operation: string;

  /**
   * Automatically show error notification to user
   * Default: false
   */
  showToast?: boolean;

  /**
   * i18n translation key for fallback error message
   * Used when specific error cannot be classified
   * Example: "components.common.errors.fetchFailed" or "pages.validation.errors.fetchFailed"
   */
  fallbackKey?: string;

  /**
   * Custom callback for additional error handling
   * Receives the final user-friendly error message
   * Example: Update component-specific error state
   */
  onError?: (message: string) => void;

  /**
   * Include operation name in error message shown to user
   * Useful for debugging but may be too technical
   * Default: false
   */
  includeOperation?: boolean;
}

/**
 * Structured error result returned by handleError
 */
export interface ErrorResult {
  /**
   * Classified error type for conditional handling
   */
  type: ErrorType;

  /**
   * User-friendly localized error message
   */
  message: string;

  /**
   * Original error object for debugging
   */
  originalError: any;

  /**
   * HTTP status code if available
   */
  statusCode?: number;
}

//
// ============================================================================
// COMPOSABLE
// ============================================================================
//

/**
 * Centralized Error Handling Composable
 *
 * Purpose:
 * Provides consistent error handling across all API composables and components.
 * Eliminates duplicated catch blocks and error parsing logic.
 *
 * Responsibilities:
 * - Parse errors from various formats (Error objects, strings, API responses)
 * - Classify errors by type (network, server, auth, abort, unknown)
 * - Generate user-friendly localized error messages
 * - Optionally display snackbar notifications
 * - Log errors to console with operation context
 * - Return structured error info for custom handling
 */
export const useErrorBoundary = () => {
  //
  // ============================================================================
  // COMPOSABLES
  // ============================================================================
  //

  // Localization
  const { t } = useI18n();

  // Snackbar notifications
  const { showSnackbar } = useSnackbar();

  //
  // ============================================================================
  // ERROR CLASSIFICATION
  // ============================================================================
  //

  /**
   * Classify error by type for appropriate handling
   *
   * @param error - Error object or string
   * @returns Classified error type
   */
  const classifyError = (error: any): ErrorType => {
    // Handle AbortError from AbortController
    if (error?.name === "AbortError") {
      return "abort";
    }

    // Extract error message from various formats.
    // Prefer the parsed response body message (data.message) over the generic
    // HTTP status string in error.message — this surfaces server-side validation
    // messages (e.g. "Test code already in use") instead of "409 Conflict".
    const errorStr = typeof error === "string" ? error : error?.data?.message || error?.message || "";
    const errorLower = errorStr.toLowerCase();

    // Extract HTTP status code
    const statusCode = error?.statusCode || error?.status || error?.response?.status;

    // Check for authentication/authorization errors
    if (statusCode === 401 || statusCode === 403) {
      return "auth";
    }

    // Check for validation / conflict errors.
    // 400 Bad Request — invalid input
    // 409 Conflict    — duplicate / constraint violation
    // 422 Unprocessable Entity — semantic validation failure
    if (statusCode === 400 || statusCode === 409 || statusCode === 422) {
      return "validation";
    }

    // Check for network connectivity issues
    if (
      errorLower.includes("failed to fetch") ||
      errorLower.includes("network") ||
      errorLower.includes("no response") ||
      errorLower.includes("cors") ||
      errorLower.includes("timeout")
    ) {
      return "network";
    }

    // Check for server errors
    // 500: Internal server error
    // 502: Bad gateway
    // 503: Service unavailable
    // 504: Gateway timeout
    if (
      statusCode >= 500 ||
      errorStr.includes("500") ||
      errorStr.includes("502") ||
      errorStr.includes("503") ||
      errorStr.includes("504") ||
      errorLower.includes("internal server error")
    ) {
      return "server";
    }

    return "unknown";
  };

  /**
   * Get localized error message based on error type
   *
   * @param type - Classified error type
   * @param fallbackKey - Custom fallback translation key
   * @param originalMessage - Original error message for unknown errors
   * @returns Localized user-friendly message
   */
  const getErrorMessage = (type: ErrorType, fallbackKey?: string, originalMessage?: string): string => {
    switch (type) {
      case "abort":
        // Silent - no message needed for user-initiated cancellation
        return "";

      case "network":
        // Network errors - suggest checking connection
        return t("pages.validation.results.pending.errors.networkError");

      case "server":
        // Server errors - suggest trying again later
        return t("pages.validation.results.pending.errors.serverError");

      case "auth":
        // Auth errors - suggest logging in again
        return t("components.common.errors.authError", "Session expired. Please log in again.");

      case "validation":
        // Validation errors - show specific message from API
        return (
          originalMessage || t("components.common.errors.validationError", "Invalid data. Please check your input.")
        );

      case "unknown":
      default:
        // Unknown errors - use fallback or show original message
        if (fallbackKey) {
          return t(fallbackKey);
        }
        // Only show original message if it's user-friendly (not stack trace)
        if (originalMessage && !originalMessage.includes("Error:") && originalMessage.length < 200) {
          return originalMessage;
        }
        return t("pages.validation.results.pending.errors.pleaseTryAgain");
    }
  };

  //
  // ============================================================================
  // MAIN ERROR HANDLER
  // ============================================================================
  //

  /**
   * Handle error with classification, logging, and optional notification
   *
   * @param error - Error object, string, or any thrown value
   * @param options - Configuration for error handling behavior
   * @returns Structured error result with type and message
   */
  const handleError = (error: any, options: ErrorHandlerOptions): ErrorResult => {
    // Step 1: Classify error type
    const type = classifyError(error);

    // Step 2: Extract original message
    const originalMessage = typeof error === "string" ? error : error?.message || error?.data?.message || "";

    // Step 3: Get user-friendly localized message
    const message = getErrorMessage(type, options.fallbackKey, originalMessage);

    // Step 4: Extract status code
    const statusCode = error?.statusCode || error?.status || error?.response?.status;

    // Step 5: Log to console for debugging
    const logPrefix = `[${options.operation}]`;
    const logMessage = type === "abort" ? "Request aborted" : `${type.toUpperCase()}: ${message}`;

    console.error(logPrefix, logMessage, error);

    // Step 6: Show toast notification if requested
    if (options.showToast && message && type !== "abort") {
      showSnackbar(options.includeOperation ? `${options.operation}: ${message}` : message, {
        color: "error",
        timeout: 2500,
        icon: "mdi-alert-circle-outline",
      });
    }

    // Step 7: Call custom error handler
    if (options.onError && message) {
      options.onError(message);
    }

    // Step 8: Return structured result
    return {
      type,
      message,
      originalError: error,
      statusCode,
    };
  };

  //
  // ============================================================================
  // PUBLIC API
  // ============================================================================
  //

  return {
    /**
     * Handle error with classification and optional notification
     */
    handleError,

    /**
     * Classify error type without handling (for conditional logic)
     */
    classifyError,

    /**
     * Get localized message for error type (for custom UIs)
     */
    getErrorMessage,
  };
};
